<template>
  <model-form
    ref="modelForm"
    :model="section"
    :handled-actions="handledActions"
    disable-language-select
    show-translation-notice
    @change="$emit('change')"
  >
    <div>
      <div class="row q-col-gutter-md items-start">
        <q-select
          v-model="section.type"
          emit-value
          map-options
          class="col-6"
          label="Type"
          :options="section.fieldOptions('type')"
          :rules="[val => val !== null || 'Field is required']"
          @update:model-value="handleSelectType"
        />
        <model-form-input
          field="name"
          class="col"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
        />
        <div class="col-auto q-mt-sm">
          <q-btn
            icon="settings"
            :disable="section.isTranslation"
            @click="$refs.sectionSettings.show()"
          >
            <english-required-tooltip v-if="section.isTranslation" />
            <q-tooltip v-else>
              Visibility Settings
            </q-tooltip>
          </q-btn>
        </div>
      </div>
      <q-banner
        v-if="!!foundNetlifyUrl"
        dense
        rounded
        class="bg-orange text-white col-12 q-mb-sm"
      >
        <template #avatar>
          <q-icon name="warning" />
        </template>
        Custom block content contains {{ foundNetlifyUrl }}
      </q-banner>
      <html-editor
        v-if="section.type === SECTION_TYPES.CUSTOM"
        :block-or-section="section"
        :custom-blocks="customBlocks"
        :base-styles="baseStyles"
        :base-class="page.scopeClass"
        :highlight="section.isTranslation"
      />
      <rich-section-editor
        v-if="section.type === SECTION_TYPES.RICH_TEXT"
        :section="section"
      />
      <poster-carousel-editor
        v-if="section.type === SECTION_TYPES.POSTERS"
        :section="section"
      />
      <showtimes-editor
        v-if="section.type === SECTION_TYPES.SHOWTIMES"
        :section="section"
      />
      <news-carousel-editor
        v-if="section.type === SECTION_TYPES.NEWS"
        :section="section"
      />
      <explore-news-editor
        v-if="section.type === SECTION_TYPES.EXPLORE_NEWS"
        :section="section"
      />
      <hero-image-editor
        v-if="section.type === SECTION_TYPES.HERO_IMAGE"
        :section="section"
      />
      <explore-movies-editor
        v-if="section.type === SECTION_TYPES.EXPLORE_MOVIES"
        :section="section"
      />
      <donation-widget-editor
        v-if="section.type === SECTION_TYPES.DONATION"
        :section="section"
      />
      <showtime-quick-selector-editor
        v-if="section.type === SECTION_TYPES.QUICK_SHOWTIME"
        :section="section"
      />
      <voucher-sales-widget-editor
        v-if="section.type === SECTION_TYPES.VOUCHERS"
        :section="section"
      />
      <gift-card-sales-widget-editor
        v-if="section.type === SECTION_TYPES.GIFT_CARDS"
        :section="section"
      />
      <marketing-signup-editor
        v-if="section.type === SECTION_TYPES.MARKETING_SIGNUP"
        :section="section"
      />
      <generic-form-editor
        v-if="section.type === SECTION_TYPES.GENERIC_FORM"
        :section="section"
      />
      <generic-carousel-editor
        v-if="section.type === SECTION_TYPES.GENERIC_CAROUSEL"
        :section="section"
      />
      <consumer-footer-editor
        v-if="section.type === SECTION_TYPES.FOOTER"
        :section="section"
      />
      <site-selector-editor
        v-if="section.type === SECTION_TYPES.SITE_SELECTOR"
        :section="section"
      />
      <site-selector-with-zones-editor
        v-if="section.type === SECTION_TYPES.SITE_SELECTOR_WITH_ZONES"
        :section="section"
      />
    </div>
    <section-settings-form
      ref="sectionSettings"
      :section="section"
    />
  </model-form>
</template>

<script>
import { date } from 'quasar'
import Page from 'src/models/page/Page'
import { SECTION_TYPES, VISIBILITY_OPTIONS } from 'src/models/page/Section'
import EnglishRequiredTooltip from 'src/modules/admin/components/EnglishRequiredTooltip'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ConsumerFooterEditor from 'src/modules/admin/section/ConsumerFooterEditor'
import DonationWidgetEditor from 'src/modules/admin/section/DonationWidgetEditor'
import ExploreMoviesEditor from 'src/modules/admin/section/ExploreMoviesEditor'
import ExploreNewsEditor from 'src/modules/admin/section/ExploreNewsEditor'
import GenericCarouselEditor from 'src/modules/admin/section/GenericCarouselEditor'
import GenericFormEditor from 'src/modules/admin/section/GenericFormEditor'
import GiftCardSalesWidgetEditor from 'src/modules/admin/section/GiftCardSalesWidgetEditor'
import HeroImageEditor from 'src/modules/admin/section/HeroImageEditor'
import MarketingSignupEditor from 'src/modules/admin/section/MarketingSignupEditor'
import NewsCarouselEditor from 'src/modules/admin/section/NewsCarouselEditor'
import PosterCarouselEditor from 'src/modules/admin/section/PosterCarouselEditor'
import RichSectionEditor from 'src/modules/admin/section/RichSectionEditor'
import SectionSettingsForm from 'src/modules/admin/section/SectionSettingsForm'
import ShowtimeQuickSelectorEditor from 'src/modules/admin/section/ShowtimeQuickSelectorEditor'
import ShowtimesEditor from 'src/modules/admin/section/ShowtimesEditor'
import SiteSelectorEditor from 'src/modules/admin/section/SiteSelectorEditor'
import SiteSelectorWithZonesEditor from 'src/modules/admin/section/SiteSelectorWithZonesEditor'
import VoucherSalesWidgetEditor from 'src/modules/admin/section/VoucherSalesWidgetEditor'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'SectionForm',
  components: {
    EnglishRequiredTooltip,
    ModelForm,
    RichSectionEditor,
    PosterCarouselEditor,
    ShowtimesEditor,
    NewsCarouselEditor,
    ExploreNewsEditor,
    HeroImageEditor,
    HtmlEditor: defineAsyncComponent(() => import('src/modules/admin/html-editor/HtmlEditor')),
    SectionSettingsForm,
    ExploreMoviesEditor,
    ConsumerFooterEditor,
    DonationWidgetEditor,
    ShowtimeQuickSelectorEditor,
    VoucherSalesWidgetEditor,
    GiftCardSalesWidgetEditor,
    MarketingSignupEditor,
    GenericFormEditor,
    GenericCarouselEditor,
    SiteSelectorEditor,
    SiteSelectorWithZonesEditor
  },
  configuration: ['currentSite'],
  props: {
    handledActions: {
      type: Array,
      required: false,
      default () {
        return ['add', 'edit']
      }
    },
    page: {
      type: Page,
      required: true
    }
  },
  emits: ['change'],
  models: {
    section: {
      page: {
        fields: ['id', 'draftId'],
        draftParent: {
          fields: ['id', 'draftId'],
          sections: {
            fields: ['id', 'draftId']
          }
        }
      },
      movies: {
        fields: 'select'
      },
      showings: {
        fields: 'select',
        movie: {
          fields: 'select'
        }
      },
      newsItemClassIds: true,
      titleClassIds: true,
      exploreTitleClassIds: true,
      exploreFeaturedTitleClassIds: true,
      showingBadgeIds: true,
      exploreFeaturedShowingBadgeIds: true,
      filterShowingBadgeIds: true,
      filterOptionShowingBadgeIds: true,
      content: true,
      styles: true,
      libraryChildSiteIds: true
    },
    customBlocks: {
      content: true,
      styles: true,
      libraryChildSiteIds: true
    }
  },
  data () {
    return {
      showStartCalendar: false,
      showEndCalendar: false
    }
  },
  computed: {
    baseStyles () {
      return [
        this.currentSite.themeColorsCss,
        this.currentSite.transformedStyles || '',
        this.page.transformedStyles || ''
      ].join(' ')
    },
    foundNetlifyUrl () {
      return this.$mh.foundNetlifyUrl(this.section.content)
    }
  },
  created () {
    _.extend(this, { date, SECTION_TYPES, VISIBILITY_OPTIONS })
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    handleSelectType (value) {
      if (value === SECTION_TYPES.CUSTOM) {
        this.section.name = null
      } else {
        this.section.name = value
      }

      if (value === SECTION_TYPES.HERO_IMAGE || value === SECTION_TYPES.GENERIC_FORM) {
        this.section.config.widthMode = 'Full Width'
      }

      if (value === SECTION_TYPES.FOOTER) {
        this.section.config.visible_for = _.intersection(this.section.config.visible_for, VISIBILITY_OPTIONS.WEB_ONLY)
      }
    }
  }
}
</script>
